import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import StudentProgressDrilldown from "../components/StudentProgressDrilldown";
import InviteParentDialog from "../components/InviteParentDialog";
import { Eye, Users, UserPlus } from "@phosphor-icons/react";

export default function Students() {
  const [students, setStudents] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [drilldownUserId, setDrilldownUserId] = useState(null);
  const [invitingStudent, setInvitingStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [s, w] = await Promise.all([
        api.get("/students"),
        api.get("/weeks"),
      ]);
      setStudents(s.data);
      setWeeks(w.data);
      setLoading(false);
    })();
  }, []);

  const totalTasks = useMemo(
    () => weeks.reduce((sum, w) => sum + (w.tasks?.length || 0), 0),
    [weeks]
  );

  const fmtDate = (iso) => {
    if (!iso) return "Never";
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    const diff = (Date.now() - d.getTime()) / 86400000;
    if (diff < 1) return "Today";
    if (diff < 2) return "Yesterday";
    if (diff < 7) return `${Math.floor(diff)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <div className="flex items-center gap-2 text-slate-500">
            <Users weight="fill" className="text-yellow-500" size={18} />
            <span className="text-xs font-bold tracking-wider uppercase">Class Roster</span>
          </div>
          <h1 className="font-outfit text-3xl sm:text-4xl font-black tracking-tight">
            {students.length} {students.length === 1 ? "student" : "students"}
          </h1>
          <p className="text-slate-600 mt-1">Tap any row to see exactly which tasks they've completed and when.</p>
        </div>

        {loading ? (
          <div className="text-slate-500" data-testid="students-loading">Loading class...</div>
        ) : students.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center text-slate-500 italic">
            No students yet. Head to Admin → Add account to create some.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="students-grid">
            {students.map((s) => {
              const verified = s.verified_tasks ?? s.completed_tasks ?? 0;
              const pending = s.pending_tasks ?? 0;
              const pct = totalTasks ? Math.round((verified / totalTasks) * 100) : 0;
              return (
                <div
                  key={s.id}
                  data-testid={`student-card-${s.id}`}
                  className="text-left bg-white rounded-3xl border border-slate-200 p-5 hover:border-slate-300 hover:shadow-sm transition group flex flex-col"
                >
                  <div className="flex items-start gap-3">
                    <img
                      src={`https://api.dicebear.com/9.x/micah/svg?seed=${encodeURIComponent(s.name)}`}
                      alt=""
                      className="w-12 h-12 rounded-full bg-slate-100 border border-slate-200"
                    />
                    <button
                      type="button"
                      onClick={() => setDrilldownUserId(s.id)}
                      data-testid={`student-open-drilldown-${s.id}`}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="font-outfit font-bold text-lg leading-tight">{s.name}</div>
                      <div className="text-xs text-slate-500 font-mono truncate">{s.username || s.email}</div>
                    </button>
                    {pending > 0 ? (
                      <span
                        className="text-[10px] font-bold tracking-wider uppercase bg-amber-100 text-amber-900 px-2 py-1 rounded-lg shrink-0"
                        data-testid={`student-pending-badge-${s.id}`}
                        title={`${pending} task(s) waiting for review`}
                      >
                        {pending} pending
                      </span>
                    ) : (
                      <Eye size={18} className="text-slate-300 group-hover:text-slate-600 mt-1" />
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-slate-600">Verified</span>
                      <span className="text-xs text-slate-500">
                        {verified}/{totalTasks} ({pct}%)
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                      {pending > 0 && (
                        <div
                          className="h-full bg-amber-300 transition-all"
                          style={{ width: `${totalTasks ? (pending / totalTasks) * 100 : 0}%` }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">Last seen · {fmtDate(s.last_login_at)}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setInvitingStudent(s); }}
                      data-testid={`invite-parent-btn-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                      title="Send a one-time invite link to this student's parent"
                    >
                      <UserPlus size={12} weight="bold" /> Invite parent
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <StudentProgressDrilldown userId={drilldownUserId} onClose={() => setDrilldownUserId(null)} />
      {invitingStudent && (
        <InviteParentDialog
          student={invitingStudent}
          open={!!invitingStudent}
          onClose={() => setInvitingStudent(null)}
        />
      )}
    </div>
  );
}
